// unique-tab: one "session owner" tab per (browser, scope) for opt-in working surfaces
// (assessment-taking / coding practice). Newest tab to claim wins; the older owner goes
// read-only and is expected to LEAVE — not to reclaim in place.
//
// TSK-11215: the election is scoped per assessment. It used to be one global key, so
// opening assessment B froze the tab already taking assessment A — which acceptance
// criterion 4 ("같은 계정으로 서로 다른 평가는 동시 진행이 가능하다") forbids.
//
// This covers tabs inside ONE browser only. The cross-device half is the server-side seat
// (X-Seat-Id + seatValid on the session-state poll). Both drive the same popup.
//
// Separate from useAuthChannel (codepresso-auth) on purpose: different channel,
// different lifecycle (long-lived here vs. one-shot there). Both share the
// same useBroadcastChannel primitive, so SSR/support-guard behavior matches.

import { ref, type Ref } from 'vue';
import type { BroadcastHandle } from './useBroadcastChannel';

const OWNER_KEY_PREFIX = 'codepresso-session-owner';
const CHANNEL_PREFIX = 'codepresso-session';

export type SessionOwnerMessage = { type: 'claim'; tabId: string };

/**
 * The key two tabs must agree on to be "the same screen". Route names carry their param names
 * as segments (`learning-proSkillAssure-assessmentId-taskId`), so stripping those segments
 * collapses a surface's index and task routes onto one scope — refreshing between them must
 * not look like a different screen.
 *
 * Exported because `app.vue` needs the SAME value for `:key` on the guard: the guard binds its
 * channel/storage key once at setup, so a guarded→guarded navigation has to remount it.
 */
export const sessionOwnerScope = (
    routeName: unknown,
    params: Record<string, unknown>,
): string => {
    const paramNames = new Set(Object.keys(params));
    const surface = String(routeName ?? '')
        .split('-')
        .filter((segment) => !paramNames.has(segment))
        .join('-');
    return `${surface}:${Object.values(params)[0] ?? ''}`;
};

/**
 * @param scope what is being worked on, e.g. `learning-proSkillAssure:123`. Tabs contend only
 *              with tabs of the SAME scope.
 */
export const useSessionOwner = (
    scope: string,
): {
    isOwner: Ref<boolean>;
    isStarted: Ref<boolean>;
    claim: () => void;
    release: () => void;
    start: () => void;
    stop: () => void;
} => {
    const ownerKey = `${OWNER_KEY_PREFIX}:${scope}`;

    /** localStorage access wrapped best-effort (Safari private mode / quota exceptions). */
    const setOwnerKey = (v: string): void => {
        try {
            localStorage.setItem(ownerKey, v);
        } catch {
            /* best-effort */
        }
    };
    const getOwnerKey = (): string | null => {
        try {
            return localStorage.getItem(ownerKey);
        } catch {
            return null;
        }
    };
    const removeOwnerKey = (): void => {
        try {
            localStorage.removeItem(ownerKey);
        } catch {
            /* best-effort */
        }
    };

    // isStarted gates the overlay: until start() has resolved ownership, isOwner
    // is meaningless — rendering `!isOwner` before that flashes the overlay on
    // the owner tab (SSR HTML included).
    const isOwner = ref(false);
    const isStarted = ref(false);
    const tabId = `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    const channel = useBroadcastChannel<SessionOwnerMessage>(`${CHANNEL_PREFIX}:${scope}`);
    let handle: BroadcastHandle<SessionOwnerMessage> | null = null;

    /** Take ownership: write the owner key, flip local state, and notify other tabs. */
    const claim = (): void => {
        if (!channel.isSupported()) {
            isOwner.value = true; // degrade open: no cross-tab coordination available
            return;
        }
        setOwnerKey(tabId);
        isOwner.value = true;
        handle?.post({ type: 'claim', tabId });
    };

    /** Give up ownership. Only clears the owner key if this tab still holds it. */
    const release = (): void => {
        if (channel.isSupported() && getOwnerKey() === tabId) {
            removeOwnerKey();
        }
        isOwner.value = false;
    };

    /** Join the leader-election channel and claim ownership. */
    const start = (): void => {
        if (!channel.isSupported()) {
            isOwner.value = true;
            isStarted.value = true;
            return;
        }
        handle = channel.open((data) => {
            if (data?.type === 'claim' && data.tabId !== tabId) {
                isOwner.value = false; // another tab just took ownership
            }
        });
        claim();
        isStarted.value = true;
    };

    /** Leave the channel, releasing ownership if held. */
    const stop = (): void => {
        release();
        handle?.close();
        handle = null;
        isStarted.value = false;
    };

    return { isOwner, isStarted, claim, release, start, stop };
};
